import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    TibboDataType,
    ValidationMessage,
    SettingState,
    SettingOption,
    ValidationMessageType,
} from '@appblocks/codegeneration/types';
import UnoWireSensorValue from '@appblocks/codegeneration/SettingsConfigurations/UnoWireSensorValue';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';

export default class UnoWireSensor extends BaseFeature {
    constructor() {
        super();
        this.name = '1-Wire/Single-Wire Sensor';
        this.id = 'unowiresensor';

        const settings = [
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.NAME,
                max: 20,
                required: true,
                default: 'sensor',
            },
            {
                id: 'SENSORTYPE',
                name: 'Sensor Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'DS18B20',
                options: [
                    {
                        name: '1-Wire Temperature Sensor (DS18B20)',
                        value: 'DS18B20',
                    },
                    {
                        name: 'DHT11 Temperature/Humidity Sensor',
                        value: 'DHT11',
                    },
                    {
                        name: 'DHT22 Temperature/Humidity Sensor',
                        value: 'DHT22',
                    },
                ],
            },
            {
                id: 'TIBBIT',
                name: 'Tibbit',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
                options: [],
            },
            {
                id: 'CHANNEL',
                name: 'Channel',
                settingType: SettingType.OPTION,
                required: true,
                default: '1',
                options: [
                    {
                        name: 'Channel 1',
                        value: '1',
                    },
                    {
                        name: 'Channel 2',
                        value: '2',
                    },
                ],
            },
            {
                id: 'VALUES',
                name: 'Sensor Values',
                settingType: SettingType.ARRAY,
                required: true,
                readonly: true,
                default: new Array<BaseFeature>(0),
                refId: 'unowiresensorval',
            },

        ];

        this.settings = this.settings.concat(settings);
    }

    validate(): Array<ValidationMessage> {
        const errors: ValidationMessage[] = [];
        const NAME = this.getSettingValue('NAME');
        const SENSORTYPE = this.getSettingValue('SENSORTYPE');
        const VALUES = this.getSettingValue('VALUES');
        const project = this.project;
        switch (SENSORTYPE) {
            case 'DHT11':
            case 'DHT22':
                if (VALUES.length !== 2) {
                    for (let i = VALUES.length; i < 2; i++) {
                        const item = new UnoWireSensorValue();
                        item.initSettings(project);
                        errors.push({
                            messageType: ValidationMessageType.ADD,
                            settingId: 'VALUES',
                            message: '',
                            featureId: this.getID(),
                            data: FeatureUtils.toJson(item),
                        });
                    }
                } else {
                    if (VALUES[0].getSettingValue('NAME') !== `${NAME}_temp`) {
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            settingId: 'NAME',
                            message: '',
                            featureId: VALUES[0].getID(),
                            data: `${NAME}_temp`,
                        });
                    }
                    if (VALUES[0].getSettingValue('DESCRIPTION') !== 'Temperature') {
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            settingId: 'DESCRIPTION',
                            message: '',
                            featureId: VALUES[0].getID(),
                            data: 'Temperature',
                        });
                    }
                    if (VALUES[1].getSettingValue('NAME') !== `${NAME}_hum`) {
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            settingId: 'NAME',
                            message: '',
                            featureId: VALUES[1].getID(),
                            data: `${NAME}_hum`,
                        });
                    }
                    if (VALUES[1].getSettingValue('DESCRIPTION') !== 'Humidity') {
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            settingId: 'DESCRIPTION',
                            message: '',
                            featureId: VALUES[1].getID(),
                            data: 'Humidity',
                        });
                    }
                }
                break;
            case 'DS18B20':
                if (VALUES.length < 1) {
                    const item = new UnoWireSensorValue();
                    item.initSettings(project);
                    errors.push({
                        messageType: ValidationMessageType.ADD,
                        settingId: 'VALUES',
                        message: '',
                        featureId: this.getID(),
                        data: FeatureUtils.toJson(item),
                    });
                } else if (VALUES.length > 1) {
                    for (let i = 1; i < VALUES.length; i++) {
                        errors.push({
                            messageType: ValidationMessageType.REMOVE,
                            settingId: undefined,
                            message: '',
                            featureId: VALUES[i].getID(),
                        });
                    }
                } else {
                    if (VALUES[0].getSettingValue('NAME') !== `${NAME}_temp`) {
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            settingId: 'NAME',
                            message: '',
                            featureId: VALUES[0].getID(),
                            data: `${NAME}_temp`,
                        });
                    }
                    if (VALUES[0].getSettingValue('DESCRIPTION') !== 'Temperature') {
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            settingId: 'DESCRIPTION',
                            message: '',
                            featureId: VALUES[0].getID(),
                            data: 'Temperature',
                        });
                    }
                }
                break;
            default:

                break;
        }
        // validate tibbits and channels

        return errors;
    }

    initSettings(project?: any, parentFeature?: any): void {
        super.initSettings(project, parentFeature, 'NAME');
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const project: ProjectConfiguration = this.project;
        if (settingId === 'TIBBIT') {
            const options = [];
            for (let i = 0; i < project.features.length; i++) {
                const feature = project.features[i];
                if (feature.id === 'tibbit_62') {
                    options.push({
                        name: feature.getSettingValue('NAME'),
                        value: feature.getID(),
                    });
                }
            }
            return options;
        }
        return super.getSettingOptions(settingId);
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;
        const SENSORTYPE = this.getSettingValue('SENSORTYPE');
        const VALUES = this.getSettingValue('VALUES');
        const CHANNEL = this.getSettingValue('CHANNEL');
        const tibbit = project.getFeature(this.getSettingValue('TIBBIT'));
        if (!tibbit) {
            return blocks;
        }
        const tibbitI2CChannel = `${tibbit.getSettingValue('NAME')}_CHANNEL_NUM`;
        const TIBBIT_NAME = tibbit.getSettingValue('NAME');
        if (SENSORTYPE === 'DHT11' || SENSORTYPE === 'DHT22') {
            const temp = VALUES[0];
            const hum = VALUES[1];
            blocks.push({
                location: 'single_wire_update',
                blockType: BlockType.CODE,
                template: `
        tbt62_sw_get_temp_hum(sw_temp_tmp, sw_hum_tmp, ${SENSORTYPE}, ${Number(CHANNEL) - 1}, ${tibbitI2CChannel})
        var_${temp.getSettingValue('NAME')}_update_completed(sw_temp_tmp)
        var_${hum.getSettingValue('NAME')}_update_completed(sw_hum_tmp)`,
            });

            blocks.push({
                location: `tbt62_${TIBBIT_NAME}_init_done`,
                blockType: BlockType.CODE,
                template: `
    'Single-Wire Sensor on tibbit 
    if tbt62_set_mode(TBT62_SINGLE_WIRE, ${Number(CHANNEL) - 1}, ${tibbitI2CChannel})=false then
        sys.debugprint("tbt62> send command fault!\\r\\n")
    end if`,
            });
        } else {
            let sensorIndex = 0;
            const parent = project.getParent(this);
            const sensors = parent.getSettingValue('SENSORS');
            for (let i = 0; i < sensors.length; i++) {
                if (sensors[i].getID() === this.getID()) {
                    break;
                }
                if (sensors[i].getSettingValue('SENSORTYPE') === SENSORTYPE) {
                    sensorIndex++;
                }
            }

            blocks.push({
                location: `tbt62_${TIBBIT_NAME}_init_done`,
                blockType: BlockType.CODE,
                template: `
    if tbt62_set_mode(TBT62_ONE_WIRE, ${Number(CHANNEL) - 1}, ${TIBBIT_NAME}_CHANNEL_NUM)=false then
        sys.debugprint("send command fault!\\r\\n")
    else
        sys.debugprint("success\\r\\n")
    end if`,
            });

            blocks.push({
                location: `tbt62_${TIBBIT_NAME}_init_done`,
                blockType: BlockType.CODE,
                template: `
    unowire_setup(${sensorIndex}, "${this.getID()}", ${Number(CHANNEL) - 1}, ${TIBBIT_NAME}_CHANNEL_NUM)`,
            });
        }

        return blocks;
    }
}
